// src/components/study/BackupCrcInput.js
//
// "Backup CRC" editor for the Add / Edit Study forms. Each backup is PICKED
// from the site's active coordinators (the `coordinators` collection, same
// source as every other coordinator picker) — never free-typed, so names always
// match the coordinator records exactly. Typing filters the list; click or
// Enter picks. "+ Add another backup CRC" for as many as needed, X to remove.
//
// The parent keeps the rows (a blank row = a picker not yet used);
// normalizeBackupCrcs() drops blanks on save. A saved name that is no longer
// an active coordinator is kept and shown as such — never silently dropped.

import { useEffect, useMemo, useState } from "react";
import { Plus, X, UserCircle2 } from "lucide-react";
import { fetchActiveCoordinators } from "../../services/coordinatorService";
import SearchableSelect from "../SearchableSelect";

const norm = (s) => String(s || "").trim().toLowerCase();

const BackupCrcInput = ({ rows, onChange, label = "Backup CRC" }) => {
  const list = useMemo(() => (rows && rows.length ? rows : [""]), [rows]);
  const [coordinators, setCoordinators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    let alive = true;
    fetchActiveCoordinators()
      .then((cs) => {
        if (!alive) return;
        setCoordinators(
          [...new Set(cs.map((c) => String(c.name || "").trim()).filter(Boolean))]
        );
      })
      .catch((err) => {
        console.error("Failed to load coordinators:", err);
        if (alive) setLoadError(true);
      })
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, []);

  const activeSet = useMemo(() => new Set(coordinators.map(norm)), [coordinators]);
  const chosen = useMemo(() => new Set(list.filter(Boolean).map(norm)), [list]);
  // Offer only coordinators not already picked in another row.
  const available = useMemo(() => coordinators.filter((n) => !chosen.has(norm(n))), [coordinators, chosen]);

  const setAt = (idx, value) => onChange(list.map((v, i) => (i === idx ? value : v)));
  const removeAt = (idx) => {
    const next = list.filter((_, i) => i !== idx);
    onChange(next.length ? next : [""]);
  };
  const addRow = () => onChange([...list, ""]);

  return (
    <div className="block">
      <span className="block text-[10px] uppercase tracking-wider font-medium text-slate-400 mb-1">
        {label}
      </span>
      <div className="space-y-1.5">
        {list.map((value, idx) =>
          value ? (
            <div
              key={`${idx}-${value}`}
              className="flex items-center gap-2 rounded-xl bg-white/[0.06] border border-white/10 px-3 py-2"
            >
              <UserCircle2 className="w-4 h-4 text-purple-300 shrink-0" />
              <span className="text-sm text-slate-100 truncate">{value}</span>
              {!loading && !loadError && !activeSet.has(norm(value)) && (
                <span className="text-[10px] text-amber-300 shrink-0">not an active coordinator</span>
              )}
              <button
                type="button"
                onClick={() => removeAt(idx)}
                title="Remove"
                className="ml-auto text-slate-500 hover:text-rose-300 shrink-0"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          ) : (
            <div key={`${idx}-empty`} className="flex items-center gap-1.5">
              <div className="flex-1">
                <SearchableSelect
                  value=""
                  options={available}
                  loading={loading}
                  onChange={(name) => setAt(idx, name)}
                  placeholder={idx === 0 ? "Type to find a coordinator" : "Another backup CRC"}
                  noMatchText="No active coordinator matches that."
                  emptyText="No other active coordinators to choose from."
                />
              </div>
              {list.length > 1 && (
                <button
                  type="button"
                  onClick={() => removeAt(idx)}
                  title="Remove"
                  className="text-slate-500 hover:text-rose-300 shrink-0"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          )
        )}
      </div>
      {loadError && (
        <p className="mt-1 text-[11px] text-rose-300">
          Couldn't load the coordinator list. Existing backups are kept; try reopening this form.
        </p>
      )}
      {list[list.length - 1] && (
        <button
          type="button"
          onClick={addRow}
          className="mt-1.5 inline-flex items-center gap-1 text-[11px] font-medium text-purple-300 hover:text-white transition"
        >
          <Plus className="w-3 h-3" />
          Add another backup CRC
        </button>
      )}
    </div>
  );
};

export default BackupCrcInput;
